<template>
    <div class="searchUser"
         v-if="canPlayForUser === true">
        <div class="search-user-panel">

            <div class="search-user-header">
                <div class="search-user-header-icon" v-if="userSelected == null">
                    <v-icon>mdi-account-switch</v-icon>
                </div>
                <div class="search-user-header-text">
                    <div class="search-user-title">{{$t('playOnBehalfOfAUser')}}</div>
                    <div class="search-user-muted" v-if="userSelected == null">{{$t('searchOneOfYourPlayers')}}</div>
                    <span class="search-user-status" v-else>
                        <span class="search-user-status-dot"></span>{{$t('playingOnBehalf')}}
                    </span>
                </div>
            </div>

            <v-autocomplete
                    v-show="userSelected == null"
                    ref="userSearch"
                    v-model="userSelected"
                    :items="users"
                    :search-input.sync="search"
                    class="search-user-input"
                    :menu-props="{contentClass: 'search-user-menu', nudgeBottom: 4}"
                    :placeholder="$t('searchOneOfYourPlayers')"
                    :hide-no-data="!loginToSearch || loginToSearch.length < 3"
                    :no-data-text="$tc('playersFound', 0, {count: 0})"
                    prepend-inner-icon="mdi-magnify"
                    append-icon=""
                    item-text="userName"
                    item-value="userId"
                    solo
                    flat
                    dense
                    hide-details
                    clearable
                    return-object>
                <template v-slot:item="{item}">
                    <div class="search-user-avatar">{{initials(item.userName)}}</div>
                    <div class="search-user-item-text">
                        <div class="search-user-item-name">
                            <span v-for="(part, index) in nameParts(item.userName)"
                                  :key="index"
                                  :class="{'search-user-match': part.match}">{{part.text}}</span>
                        </div>
                        <div class="search-user-muted">ID {{item.userId}}</div>
                    </div>
                </template>
                <template v-slot:append-item>
                    <div class="search-user-count" v-if="users.length > 0">
                        {{$tc('playersFound', users.length, {count: users.length})}}
                    </div>
                </template>
            </v-autocomplete>

            <template v-if="userSelected != null">
                <div class="search-user-card">
                    <div class="search-user-card-row">
                        <div class="search-user-avatar search-user-avatar--large">{{initials(userSelected.userName)}}</div>
                        <div class="search-user-item-text">
                            <div class="search-user-card-name">{{userSelected.userName}}</div>
                            <div class="search-user-muted">ID {{userSelected.userId}}</div>
                        </div>
                        <button type="button" class="search-user-icon-btn"
                                :title="$t('changeUser')" :aria-label="$t('changeUser')"
                                @click="changeUser()">
                            <v-icon>mdi-swap-horizontal</v-icon>
                        </button>
                    </div>
                    <div class="search-user-balance">
                        <div>
                            <div class="search-user-muted">{{$t('balance')}}</div>
                            <div class="search-user-balance-value">{{formatPrice(selectedUserBalance)}}</div>
                        </div>
                        <button type="button" class="search-user-icon-btn search-user-icon-btn--primary"
                                :title="$t('refreshBalance')" :aria-label="$t('refreshBalance')"
                                @click="updateSportBalance()">
                            <v-icon>mdi-refresh</v-icon>
                        </button>
                    </div>
                </div>

                <!-- Offered only to an agency, when the skin enables it (enableAgencyAutoRecharge) -->
                <label class="search-user-recharge" v-if="canAutoRecharge === true">
                    <span>{{$t('automaticRecharge')}}</span>
                    <span class="search-user-switch">
                        <input type="checkbox" v-model="autoRecharge" @change="updateAutoRechargeMode">
                        <span class="search-user-slider"></span>
                    </span>
                </label>

                <button type="button" class="search-user-stop" @click="resetUser()">
                    <v-icon>mdi-logout</v-icon>
                    {{$t('stopPlayingOnBehalf')}}
                </button>
            </template>

        </div>
    </div>
</template>

<script>
    import {mapActions, mapGetters} from "vuex";
    import axios from "axios";
    import {getProxyAccessToken} from "@/plugins/cookie-plugins";

    const AGENCY_TYPE = 4;

    export default {
        name: "SearchUser",
        components: {},
        data: () => ({
            loginToSearch: null,
            userSelected: null,
            users: [],
            btnLoading: false,
            selectedUserBalance: null,
            autoRecharge: false
        }),
        mounted() {

        },
        beforeDestroy() {
        },
        watch: {
            async userSelected(newValue, oldValue) {

                if (newValue == null) {
                    await this.setUserIdOfUserToPlayBetFor(null);
                    await this.setLoginOfUserToPlayBetFor(null);
                    this.selectedUserBalance = null;
                    this.resetUser();
                } else {
                    await this.setUserIdOfUserToPlayBetFor(newValue.userId);
                    await this.setLoginOfUserToPlayBetFor(newValue.userName);
                    //this.updateBalanceForSelectedUser();
                    this.updateSportBalance();
                }
            }
        },
        computed: {
            ...mapGetters({
                'getType': 'user/getType',
                'getCashier': 'user/getCashier',
                'getEnableAgencyCanPlayForUser': 'settings/getEnableAgencyCanPlayForUser',
                'getEnableAgencyAutoRecharge': 'settings/getEnableAgencyAutoRecharge',
                'getEnableCashierCanPlayForUser': 'settings/getEnableCashierCanPlayForUser',
                'getProxyUrl': 'settings/getProxyUrl'
            }),
            search: {
                get() {
                    return this.loginToSearch
                },

                set(searchInput) {
                    if (this.loginToSearch !== searchInput) {
                        this.loginToSearch = searchInput
                        this.searchUser()
                    }
                }
            },

            isAgencyLogged: function () {

                let isAgencyLogged = false;

                if (this.getType != null && this.getType === AGENCY_TYPE) {
                    isAgencyLogged = true;
                }
                return isAgencyLogged
            },

            isCashierLogged: function () {
                return this.getCashier === true;
            },

            // An agency with enableAgencyCanPlayForUser, or a cashier with enableCashierCanPlayForUser
            canPlayForUser: function () {
                return (this.isAgencyLogged === true && this.getEnableAgencyCanPlayForUser === true)
                    || (this.isCashierLogged === true && this.getEnableCashierCanPlayForUser === true);
            },

            // Automatic recharge: agencies only, never a cashier
            canAutoRecharge: function () {
                return this.getEnableAgencyAutoRecharge === true
                    && this.isAgencyLogged === true
                    && this.isCashierLogged !== true;
            }
        },
        methods: {
            ...mapActions({
                'setUserIdOfUserToPlayBetFor': 'user/setUserIdOfUserToPlayBetFor',
                'setLoginOfUserToPlayBetFor': 'user/setLoginOfUserToPlayBetFor',
                'fetchUserBalance': "user/fetchUserBalance",
                'fetchUserSportBalance': 'user/fetchUserSportBalance',
                'setEnableAutoRecharge': 'settings/setEnableAutoRecharge',
                'getUserSportBalance': 'user/getUserSportBalance'
            }),

            async resetUser() {
                this.loginToSearch = null;
                this.userSelected = null;
                this.users = [];
                this.btnLoading = false
                this.selectedUserBalance = null;
                this.autoRecharge = false;
            },

            async changeUser() {
                await this.resetUser();
                // Vuetify's click-outside blurs the field after this click (setTimeout + requestAnimationFrame):
                // the focus goes after it, or it is lost
                setTimeout(() => requestAnimationFrame(() => {
                    if (this.$refs.userSearch) {
                        this.$refs.userSearch.focus();
                    }
                }), 0);
            },

            initials(userName) {
                return (userName || '').slice(0, 2).toUpperCase();
            },

            // Splits the user name around the searched text, to highlight it in the list
            nameParts(userName) {
                const name = userName || '';
                const term = (this.loginToSearch || '').toLowerCase();
                const start = term ? name.toLowerCase().indexOf(term) : -1;
                if (start < 0) {
                    return [{text: name, match: false}];
                }
                const end = start + term.length;
                return [
                    {text: name.slice(0, start), match: false},
                    {text: name.slice(start, end), match: true},
                    {text: name.slice(end), match: false}
                ].filter(part => part.text !== '');
            },

            async searchUser() {
                const vm = this;
                // The users are searched only through the proxy: the direct sircms call is deprecated
                if (this.getProxyUrl == null) {
                    console.warn('SearchUser: no proxy url configured, user search disabled');
                    return;
                }
                vm.btnLoading = true;

                const minSearchLength = 3;
                if (vm.loginToSearch && vm.loginToSearch.length >= minSearchLength) {
                    try {
                        const endpointUrl = this.constructEndpointUrl(vm.loginToSearch);
                        const response = await this.fetchUserData(endpointUrl);
                        vm.users = this.formatUserList(response.data);
                    } catch (error) {
                        console.error(error);
                        vm.resetUser();
                    } finally {
                        vm.btnLoading = false;
                    }
                }
            },

            constructEndpointUrl(loginToSearch) {
                // Only players can be played for, and never a cancelled account
                const filters = 'profileType=PLAYER&ignoreCancelled=true';
                return `${this.getProxyUrl}/user-account/signup/admin/net/entities?${filters}&likeUserName=${loginToSearch}`;
            },

            async fetchUserData(url) {
                const headers = {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${this.getToken()}`
                };
                return await axios({method: 'get', url, headers});
            },

            formatUserList(responseBody) {
                // The proxy answers with the paginated envelope {total, pageNumber, nPerPage, data: [...]}
                const userList = responseBody?.data;
                if (!Array.isArray(userList)) {
                    return [];
                }
                return userList.map(user => ({
                    userName: user.userName,
                    userId: user.userId,
                    customerId: user.customerId
                }));
            },

            getToken() {
                return getProxyAccessToken()
            },

            async updateBalanceForSelectedUser() {

                const vm = this;
                if (this.userSelected != null) {

                    try {

                        const resp = await this.fetchUserBalance({
                            customerId: this.userSelected.customerId,
                            userId: this.userSelected.userId
                        });

                        if (resp != null) {
                            let wallets = resp.data
                            wallets.forEach(function (item) {
                                if (item.walletGroup.mainWallet == true) {
                                    vm.selectedUserBalance = item.balance
                                }
                            });
                        }
                    } catch (e) {
                        console.log(e);
                        vm.resetUser();
                    }
                }
            },
            async updateSportBalance() {

                const vm = this;
                if (this.userSelected != null) {

                    try {
                        let totalBalance = await this.getUserSportBalance(
                            {
                                customerId: this.userSelected.customerId,
                                userId: this.userSelected.userId
                            });

                        this.selectedUserBalance = totalBalance;

                    } catch (e) {
                        console.log(e);
                        vm.resetUser();
                    }
                }
            },
            formatPrice(value) {
                let val = (value / 1).toFixed(2)
                return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "")
            },
            updateAutoRechargeMode() {
                // console.log('new mode is');
                // console.log(this.autoRecharge);
                this.setEnableAutoRecharge(this.autoRecharge);
            }
        }
    }
</script>

<style>

    /* The autocomplete menu is rendered at the v-app root, out of reach of scoped styles:
       background and text come from the platform theme, so they stay readable on every skin */
    .bet-console-prefix .search-user-menu {
        border: 1px solid var(--bg-card-hover);
        border-radius: 8px;
    }

    .bet-console-prefix .search-user-menu .v-list,
    .bet-console-prefix .search-user-menu .v-list-item,
    .bet-console-prefix .search-user-menu .theme--dark.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled) {
        background: var(--bg-panel) !important;
        color: var(--text-platform) !important;
        border-bottom: 0;
    }

    .bet-console-prefix .search-user-menu .v-list {
        padding: 4px 0;
    }

    .bet-console-prefix .search-user-menu .v-list-item {
        min-height: 48px;
        padding: 0 12px;
    }

    /* Rows of the menu and selected player. Not scoped: Vuetify's list reuses the DOM element of the
       "no players found" row for the first result, and a reused element never gets the scope attribute */
    .bet-console-prefix .search-user-avatar {
        width: 30px;
        height: 30px;
        margin-right: 10px;
        border-radius: 50%;
        background: var(--color-primary-op);
        color: var(--color-primary);
        font-size: var(--font-size-2xl);
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .bet-console-prefix .search-user-avatar--large {
        width: 40px;
        height: 40px;
        font-size: var(--font-size-4xl);
    }

    .bet-console-prefix .search-user-item-text {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .bet-console-prefix .search-user-item-name {
        font-size: var(--font-size-4xl);
        color: var(--text-platform);
    }

    .bet-console-prefix .search-user-match {
        color: var(--color-primary);
        font-weight: 600;
    }

    .bet-console-prefix .search-user-muted {
        color: var(--text-op-50-platform);
        font-size: var(--font-size-2xl);
    }

    .bet-console-prefix .search-user-count {
        padding: 6px 12px 2px;
        margin-top: 4px;
        border-top: 1px solid var(--bg-card-hover);
        color: var(--text-op-50-platform);
        font-size: var(--font-size-xl);
    }

</style>

<style scoped>

    /* Same card as the other widgets of the right column (cart, insert booking bet) */
    .bet-console-prefix .search-user-panel {
        background: var(--sidebar);
        border-radius: 12px;
        padding: 14px;
        color: var(--text-platform);
        font-size: var(--font-size-3xl);
    }

    .bet-console-prefix .search-user-header {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
    }

    .bet-console-prefix .search-user-header-icon {
        width: 30px;
        height: 30px;
        margin-right: 10px;
        border-radius: 8px;
        background: var(--color-primary-op);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .bet-console-prefix .search-user-header-icon .v-icon {
        color: var(--color-primary);
        font-size: var(--font-size-7xl);
    }

    .bet-console-prefix .search-user-header-text {
        flex: 1;
        min-width: 0;
    }

    .bet-console-prefix .search-user-title {
        font-size: var(--font-size-4xl);
        font-weight: 600;
    }

    .bet-console-prefix .search-user-status {
        display: inline-flex;
        align-items: center;
        margin-top: 6px;
        padding: 3px 8px;
        border-radius: 999px;
        background: var(--pending-background);
        color: var(--warning);
        font-size: var(--font-size-xl);
        white-space: nowrap;
    }

    .bet-console-prefix .search-user-status-dot {
        width: 6px;
        height: 6px;
        margin-right: 6px;
        border-radius: 50%;
        background: var(--warning);
    }

    /* Search field */
    .bet-console-prefix .search-user-input ::v-deep .v-input__slot {
        min-height: 40px;
        background: var(--bg-card) !important;
        border: 1px solid var(--bg-card-hover);
        border-radius: 8px;
    }

    .bet-console-prefix .search-user-input.v-input--is-focused ::v-deep .v-input__slot {
        border-color: var(--color-primary);
    }

    .bet-console-prefix .search-user-input ::v-deep input,
    .bet-console-prefix .search-user-input ::v-deep .v-icon {
        color: var(--text-platform) !important;
        font-size: var(--font-size-4xl);
    }

    .bet-console-prefix .search-user-input ::v-deep input::placeholder {
        color: var(--text-op-50-platform) !important;
    }

    /* Selected player */
    .bet-console-prefix .search-user-card {
        background: var(--bg-card);
        border: 1px solid var(--bg-card-hover);
        border-radius: 10px;
        padding: 12px;
    }

    .bet-console-prefix .search-user-card-row {
        display: flex;
        align-items: center;
    }

    .bet-console-prefix .search-user-card-name {
        font-size: var(--font-size-5xl);
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .bet-console-prefix .search-user-balance {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid var(--bg-card-hover);
    }

    .bet-console-prefix .search-user-balance-value {
        font-size: var(--font-size-8xl);
        font-weight: 600;
    }

    .bet-console-prefix .search-user-icon-btn {
        width: 30px;
        height: 30px;
        margin-left: 8px;
        border-radius: 8px;
        border: 1px solid var(--bg-card-hover);
        background: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        cursor: pointer;
    }

    .bet-console-prefix .search-user-icon-btn .v-icon {
        color: var(--text-op-50-platform);
        font-size: var(--font-size-6xl);
    }

    .bet-console-prefix .search-user-icon-btn--primary {
        border-color: transparent;
        background: var(--color-primary-op);
    }

    .bet-console-prefix .search-user-icon-btn--primary .v-icon {
        color: var(--color-primary);
    }

    .bet-console-prefix .search-user-recharge {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 14px 2px;
        cursor: pointer;
    }

    /* Same switch as the "accept odds" one in ToolbarSettings */
    .bet-console-prefix .search-user-switch {
        position: relative;
        width: 32px;
        height: 17px;
        flex-shrink: 0;
    }

    .bet-console-prefix .search-user-switch input {
        opacity: 0;
        width: 0;
        height: 0;
    }

    .bet-console-prefix .search-user-slider {
        position: absolute;
        inset: 0;
        background: #444;
        border-radius: 20px;
        transition: 0.2s;
    }

    .bet-console-prefix .search-user-slider:before {
        position: absolute;
        content: "";
        height: 11px;
        width: 11px;
        left: 3px;
        bottom: 3px;
        background: white;
        border-radius: 50%;
        transition: 0.2s;
    }

    .bet-console-prefix .search-user-switch input:checked + .search-user-slider {
        background: var(--color-accent);
    }

    .bet-console-prefix .search-user-switch input:checked + .search-user-slider:before {
        transform: translateX(15px);
    }

    .bet-console-prefix .search-user-stop {
        width: 100%;
        height: 38px;
        border-radius: 8px;
        border: 1px solid var(--bg-card-hover);
        background: transparent;
        color: var(--text-platform);
        font-size: var(--font-size-3xl);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }

    .bet-console-prefix .search-user-stop .v-icon {
        margin-right: 8px;
        color: var(--text-platform);
        font-size: var(--font-size-6xl);
    }

    .bet-console-prefix .search-user-stop:hover,
    .bet-console-prefix .search-user-icon-btn:hover {
        background: var(--bg-card-hover);
    }

</style>
